// Prefetch on intent (CHAT-PERF): hovering or focusing a sidebar channel or topic loads what
// opening it would show, so the click renders from cache. Fresh data (inside the queries'
// 30 s staleTime) is not fetched again, so sweeping the pointer down the sidebar costs at most
// one round of calls per row.
import type { QueryClient } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { orderedTopics } from "./chat-model";
import { channelQuery, latestMessagesQuery, messagesQuery, topicsQuery } from "./chat-queries";

/** How many topics a DM's stream reads (channel-screen.tsx `INTERLEAVED_TOPICS`). */
export const STREAM_TOPICS = 8;

/**
 * A channel: its header, its topics, and what it opens on — the first page of its own
 * conversation (CHAT-TOPICS-SEPARATE), or for a DM the first page of its stream's topics.
 */
export function prefetchChannel(qc: QueryClient, api: Api, co: string, channelId: string): void {
  void Promise.all([
    qc.ensureQueryData(channelQuery(api, co, channelId)),
    qc.ensureQueryData(topicsQuery(api, co, channelId)),
  ])
    .then(([ch, t]) => {
      if (ch.kind === "dm" || ch.kind === "group_dm") {
        for (const topic of orderedTopics(t.data).slice(0, STREAM_TOPICS))
          void qc.prefetchQuery(latestMessagesQuery(api, co, topic.thread_id));
        return;
      }
      const main = t.data.find((x) => x.is_main);
      if (main)
        void qc.prefetchInfiniteQuery(messagesQuery(api, co, main.thread_id)).catch(() => {});
    })
    .catch(() => {});
}

/** A topic: page 1 of its messages. */
export function prefetchTopic(qc: QueryClient, api: Api, co: string, threadId: string): void {
  void qc.prefetchInfiniteQuery(messagesQuery(api, co, threadId)).catch(() => {});
}
